import { Grid, Paper, Box, TextField } from "@mui/material";
import LinearProgress from "@mui/material/LinearProgress";
import { NoRowOverlay } from "../../table/NoRowOverlay";
import Button from "@mui/material/Button";
import * as React from "react";
import { useSelector } from "react-redux";
import { store } from "../../../store";
import { getOrder, editOrder, deleteOrder } from "../../../redux/actions/order";
import { useLocation, useNavigate } from "react-router-dom";
import OrderTable from "../../ui/OrderTable";
import EditDateDialog from "../../ui/dialogs/EditDateDialog";
import Swal from "sweetalert2";
import FormControlLabel from "@mui/material/FormControlLabel";
import Checkbox from "@mui/material/Checkbox";
import ReadOnlyTextField from "../../ui/ReadOnlyTextField";
import UserInfo from "../../ui/UserInfo";
import {
  fetchOrder,
  updateOrderField,
  updateOrderStatus,
} from "../../../services/orderService";
import {
  showErrorAlert,
  showSuccessAlert,
  showWarningAlert,
} from "../../ui/utils/AlertUtils";
import { useTranslation } from "react-i18next";
import LocalizedDataGrid from "../../ui/datagrid/LocalizedDataGrid";
import EditQuantityDialog from "../../ui/dialogs/EditQuantityDialog";
import AddressContactInfo from "../../ui/AddressContactInfo";
import { getDraftShipmentByOrderId } from "../../../services/shipmentService";
import { printOrderDetails } from "../../ui/PrintFunctions";
import OrderPageHeader from "./order/shared/OrderPageHeader";
import {
  MaterialOrderGridGroup,
  materialOrderGridListSx,
} from "../../ui/MaterialOrderGridLayout";

export const ScreenOrderInfo = (props) => {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const orderId = location.state?.orderId;
  const selectedShipmentId = location.state?.shipmentId;
  const studyId = location.state?.studyId;
  const order = useSelector((state) => state.order.order);
  const user = useSelector((state) => state.user.user);
  const adminUser = user.role === "admin";
  const cdlUser = user.organization === "CDL";
  const centerUser = user.organization === "Center";
  const [isLoading, setIsLoading] = React.useState(true);
  const [dialogOpen, setDialogOpen] = React.useState(false);
  const [selectedRow, setSelectedRow] = React.useState(null);
  const [quantity, setQuantity] = React.useState(0);
  const [draftShipment, setDraftShipment] = React.useState(null);
  const [confirmationDateDialogOpen, setConfirmationDateDialogOpen] =
    React.useState(false);
  const [newConfirmationDate, setNewConfirmationDate] = React.useState(
    order?.confirmationDate || null,
  );
  const [fieldToUpdate, setFieldToUpdate] = React.useState(null);
  const centerNumber = order?.Center?.centerNumber;
  const studyCode = cdlUser
    ? order?.Center?.Study?.cdlStudyCode
    : order?.Center?.Study?.sponsorStudyCode;
  // eslint-disable-next-line
  const sponsorStudyCode = order?.Center?.Study?.sponsorStudyCode;
  // eslint-disable-next-line
  const centerId = location.state?.centerId || order.centerId;
  const [comment, setComment] = React.useState("");
  const [cdlComment, setCdlComment] = React.useState("");
  const [pendingShipmentId, setPendingShipmentId] = React.useState(null);
  const fullAddress = AddressContactInfo({ order });
  const handleDownloadOrderDetails = () => {
    printOrderDetails(order, studyCode, centerNumber);
  };

  // Returns true if total shipped quantity is less than order total quantity
  const getTotalShippedQuantity = (order) => {
    if (!order || !Array.isArray(order.shipments)) return 0;
    return order.shipments.reduce(
      (acc, shipment) => acc + shipment.shippedQuantity,
      0,
    );
  };

  const isBackOrder = (order) => {
    if (
      !order ||
      !Array.isArray(order.shipments) ||
      order.shipments.length === 0
    )
      return false;
    return getTotalShippedQuantity(order) < order.totalQuantity;
  };

  const isBackOrderViaOrderStatus = (order) => {
    if (
      !order ||
      !Array.isArray(order.shipments) ||
      order.shipments.length === 0
    )
      return false;
    return order.status.includes("1/2");
  };

  // Handler to remove back order status
  const onRemoveBackOrderStatus = async () => {
    if (!order) return;
    const result = await Swal.fire({
      title: t("removeBackOrderStatus"),
      text:
        t("confirmRemoveBackOrderStatus") ||
        "Are you sure you want to remove the Back Order status?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: t("confirm") || "Confirm",
      cancelButtonText: t("cancel") || "Cancel",
      confirmButtonColor: "#1e3179",
      cancelButtonColor: "#d33",
    });
    if (!result.isConfirmed) return;

    // Remove '1/2' from status (and trim)
    const newStatus = order.status.replace(/\s*1\/2/g, "").trim();

    try {
      await updateOrderStatus(orderId, newStatus);
      showSuccessAlert(t("orderUpdatedSuccessfully"));
      store.dispatch(getOrder(orderId));
    } catch (error) {
      showErrorAlert(t("orderUpdateError"));
      console.error("Failed to update order status:", error);
    }
  };

  const handleSaveDate = async () => {
    if (!fieldToUpdate) {
      console.error("No field specified for update.");
      return;
    }

    await updateOrderField(orderId, fieldToUpdate, newConfirmationDate);
    showSuccessAlert(t("dateUpdated"));
    setConfirmationDateDialogOpen(false);
    store.dispatch(getOrder(orderId));
  };

  const cancelOrder = async () => {
    try {
      const result = await Swal.fire({
        title: t("cancelOrderTitle"),
        text: t("cancelOrderText"),
        icon: "warning",
        input: "text",
        inputPlaceholder: t("enterCommentPlaceholder"),
        showCancelButton: true,
        confirmButtonText: t("confirmCancelOrder"),
        confirmButtonColor: "#1e3179",
        cancelButtonText: t("keepOrder"),
        inputValidator: (value) => {
          if (!value) {
            return t("specifyReason");
          }
        },
      });

      if (result.isConfirmed) {
        const comment = result.value;

        // Fetch the current order data
        const orderData = await fetchOrder(orderId);
        const existingComment = orderData.comment || "";
        const newComment = existingComment + " / cancelled: " + comment;

        // Update the order
        await updateOrderStatus(orderId, "cancelled", null, null, newComment);
        showSuccessAlert(t("orderCancelledSuccess")).then(() => {
          window.location.reload();
        });
      } else {
        console.error("Failed to update order");
      }
    } catch (error) {
      console.error("An error occurred while updating the order:", error);
    }
  };

  const handleDeleteOrder = async () => {
    try {
      const result = await Swal.fire({
        title: t("deleteOrderTitle"),
        text: t("deleteOrderText"),
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: t("confirmDeleteOrder"),
        confirmButtonColor: "#d33",
        cancelButtonText: t("keepOrder"),
      });
      if (result.isConfirmed) {
        await store.dispatch(deleteOrder(orderId));
        showSuccessAlert(t("orderDeletedSuccess")).then(() => {
          navigate("/orders");
        });
      }
    } catch (error) {
      console.error("An error occurred while deleting the order:", error);
      showErrorAlert(t("orderDeleteError"));
    }
  };

  const handleShipment = () => {
    navigate("/shipping/new", {
      state: {
        order,
        orderId,
        centerNumber,
        studyCode,
        studyId,
        centerId,
        newShipment: draftShipment,
      },
    });
  };

  const handleAOR = () => {
    if (!pendingShipmentId) {
      showErrorAlert(t("errorOccured"));
      return;
    }

    navigate("/aor-form", {
      state: {
        orderId,
        order,
        centerNumber,
        studyCode,
        shipmentId: pendingShipmentId,
        centerId,
        studyId,
      },
    });
  };

  const getStatusMessage = (status, shipments) => {
    if (status === "placed") {
      return t("statusMessages.placed");
    }

    if (status === "confirmed") {
      return t("statusMessages.confirmed");
    }

    if (shipments?.length === 1) {
      if (shipments[0].deliveryDate && !shipments[0].confirmedDeliveryDate) {
        return t("statusMessages.delivered");
      }
      return shipments[0].deliveryDate ? null : t("statusMessages.shipped");
    }

    const hasUndeliveredShipment = shipments?.some(
      (shipment) => !shipment.deliveryDate,
    );

    if (hasUndeliveredShipment) {
      return t("statusMessages.shipped");
    }

    const hasUnvalidatedShipment = shipments?.some(
      (shipment) => shipment.deliveryDate && !shipment.confirmedDeliveryDate,
    );

    return hasUnvalidatedShipment ? t("statusMessages.delivered") : null;
  };

  const statusMessage = getStatusMessage(order?.status, order?.shipments);

  const handleCellDoubleClick = (params) => {
    if (adminUser) {
      setSelectedRow(params.row);
      setDialogOpen(true);
    }
  };

  const handleDialogClose = () => {
    setDialogOpen(false);
    setSelectedRow(null);
  };

  const handleSaveEditQuantity = async () => {
    const body = {
      quantity: Number(quantity),
    };
    store.dispatch(editOrder(selectedRow.id, body));
    await showSuccessAlert(t("quantityUpdatedSuccess"));
    handleDialogClose();
  };

  const formatDate = (dateString) => {
    const options = { year: "numeric", month: "2-digit", day: "2-digit" };
    return new Date(dateString).toLocaleDateString(undefined, options);
  };

  const materialsTypeMaterial = order?.Supplies?.filter(
    (supply) => supply.Material.type === "material",
  );
  const materialsTypeSupply = order?.Supplies?.filter(
    (supply) => supply.Material.type === "supply",
  );
  const materialsTypeReagent = order?.Supplies?.filter(
    (supply) => supply.Material.type === "reagent",
  );
  const materialsTypeDocuments = order?.Supplies?.filter(
    (supply) => supply.Material.type === "document",
  );

  const CustomDataGrid = ({ rows = [], headerName }) => {
    const columns = [
      {
        field: "materialName",
        headerName: headerName,
        flex: 0.8,
        sortable: false,
        filterable: false,
        disableColumnMenu: true,
        valueGetter: (params) => params.row.Material.name,
      },
      {
        field: "quantity",
        headerName: t("quantity"),
        flex: 0.2,
        sortable: false,
        filterable: false,
        disableColumnMenu: true,
      },
    ];

    if (!rows || rows.length === 0) {
      return null;
    }

    return (
      <MaterialOrderGridGroup>
        <LocalizedDataGrid
          slots={{
            noRowsOverlay: NoRowOverlay,
            loadingOverlay: LinearProgress,
          }}
          rows={rows}
          columns={columns}
          initialState={{
            pagination: {
              paginationModel: { page: 0, pageSize: 30 },
            },
          }}
          disableRowSelectionOnClick
          pageSizeOptions={[30, 50]}
          rowHeight={40}
          hideFooter
          autoHeight
          onCellDoubleClick={handleCellDoubleClick}
        />
      </MaterialOrderGridGroup>
    );
  };

  const confirmOrder = async () => {
    showWarningAlert(t("confirmOrderWarning")).then(async (result) => {
      if (result.isConfirmed) {
        const confirmationDate = new Date();
        const confirmedById = user.id;

        try {
          await updateOrderStatus(
            orderId,
            "confirmed",
            confirmedById,
            confirmationDate,
          );
          showSuccessAlert(t("orderConfirmedSuccess")).then(() => {
            window.location.reload();
          });
        } catch (error) {
          console.error("An error occurred while updating the order:", error);
        }
      }
    });
  };

  React.useEffect(() => {
    if (order) {
      getDraftShipmentByOrderId(orderId)
        .then((draftShipment) => {
          if (draftShipment) {
            draftShipment.productionsSent = JSON.parse(draftShipment.draftData);
            setDraftShipment(draftShipment);
          }
        })
        .catch((error) => {
          console.error(
            "An error occurred while fetching the draft shipment:",
            error,
          );
        });
    }
  }, [order, orderId]);

  React.useEffect(() => {
    store.dispatch(getOrder(orderId)).then(() => setIsLoading(false));
  }, [orderId]);

  React.useEffect(() => {
    // Initialize the comment state when the component mounts
    if (order && order.comment) {
      setComment(order.comment);
    } else {
      setComment("");
    }
    if (order && order.cdlComment) {
      setCdlComment(order.cdlComment);
    } else {
      setCdlComment("");
    }
  }, [order]);

  React.useEffect(() => {
    if (selectedRow) {
      setQuantity(selectedRow.quantity);
    }
  }, [selectedRow]);

  React.useEffect(() => {
    const shipments = Array.isArray(order?.shipments) ? order.shipments : [];

    const selectedShipment = shipments.find(
      (shipment) => String(shipment?.id) === String(selectedShipmentId),
    );

    if (
      selectedShipment &&
      (selectedShipment?.confirmedDeliveryDate == null ||
        selectedShipment?.confirmedDeliveryDate === false)
    ) {
      setPendingShipmentId(selectedShipment.id);
      return;
    }

    const firstPendingShipmentId =
      shipments.find(
        (shipment) =>
          shipment?.confirmedDeliveryDate == null ||
          shipment?.confirmedDeliveryDate === false,
      )?.id ?? null;

    setPendingShipmentId(firstPendingShipmentId);
  }, [order, order?.shipments?.length, selectedShipmentId]);

  if (isLoading) {
    return <div>Loading...</div>;
  }

  return (
    <Grid item xs={12} sx={{ mt: 2 }}>
      <Paper
        sx={{
          display: "flex",
          flexDirection: "column",
        }}>
        <div style={{ width: "100%", height: "auto" }}>
          <OrderPageHeader
            title={`Order ${order?.orderNumber || ""}`}
            downloadLabel="Download Order"
            onDownload={handleDownloadOrderDetails}
            downloadDisabled={!order}
          />
          <OrderTable
            order={order}
            studyCode={studyCode}
            centerNumber={centerNumber}
            centerId={order.centerId}
            cdlUser={cdlUser}
            studyId={studyId}
          />
          {centerUser && statusMessage && (
            <div
              style={{
                color: "#0c5460",
                backgroundColor: "#d1ecf1",
                borderColor: "#bee5eb",
                padding: "10px",
                margin: "15px",
                border: "1px solid transparent",
                borderRadius: "4px",
                textAlign: "center",
              }}>
              {statusMessage}
            </div>
          )}

          {/* AOR Button */}
          {centerUser && Boolean(pendingShipmentId) && (
            <Button
              variant="contained"
              color="primary"
              style={{
                padding: "16px 88px",
                marginTop: "1rem",
                display: "block",
                marginLeft: "auto",
                marginRight: "auto",
              }}
              onClick={handleAOR}>
              {t("confirmReception")}
            </Button>
          )}

          {order.status === "cancelled" && (
            <div
              style={{
                padding: "15px",
                margin: "20px",
                border: "1px solid transparent",
                borderRadius: "4px",
                color: "#a94442",
                backgroundColor: "#f2dede",
                borderColor: "#ebccd1",
                textAlign: "center",
              }}>
              {t("orderCancelled")}
            </div>
          )}

          {cdlUser &&
            order.status !== "placed" &&
            order.status !== "cancelled" &&
            order.shipments &&
            order.shipments.reduce(
              (sum, shipment) => sum + shipment.shippedQuantity,
              0,
            ) < order.totalQuantity && (
              <Button
                variant="contained"
                color="primary"
                style={{
                  padding: "16px 88px",
                  marginTop: "1rem",
                  display: "block",
                  marginLeft: "auto",
                  marginRight: "auto",
                }}
                onClick={handleShipment}>
                {draftShipment ? t("draft") : t("addNewShipment")}
              </Button>
            )}

          {/* Show back order row if isBackOrder(order) is true */}
          {/* ------------------------------------------------- */}
          {cdlUser && isBackOrderViaOrderStatus(order) && (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                backgroundColor: "#fff3cd",
                color: "#856404",
                border: "1px solid #ffeeba",
                borderRadius: "4px",
                padding: "5px 20px",
                margin: "15px 18px 0px 18px",
                fontWeight: 500,
              }}>
              <span>Back Order</span>

              <Button
                variant="outlined"
                color="warning"
                onClick={onRemoveBackOrderStatus}>
                {t("removeBackOrderStatus") || "Remove Back Order Status"}
              </Button>
            </div>
          )}

          <ReadOnlyTextField
            title={t("shippingAddress").toUpperCase()}
            value={fullAddress}
            multiline
            maxRows={4}
          />

          <Box sx={{ p: 2 }}>
            <Box sx={materialOrderGridListSx}>
              <TextField
                value={t("materialOrder").toUpperCase()}
                InputProps={{
                  readOnly: true,
                }}
                inputProps={{
                  style: { textAlign: "center" },
                }}
                fullWidth
              />
              <CustomDataGrid
                rows={materialsTypeMaterial}
                headerName={t("material")}
              />
              <CustomDataGrid
                rows={materialsTypeSupply}
                headerName={t("supply")}
              />
              <CustomDataGrid
                rows={materialsTypeReagent}
                headerName={t("reagent")}
              />
              <CustomDataGrid
                rows={materialsTypeDocuments}
                headerName={t("document")}
              />
            </Box>
          </Box>
          <Box sx={{ px: 2 }}>
            <FormControlLabel
              control={
                <Checkbox checked={order.isFirstSupply} name="isFirstSupply" />
              }
              label={t("firstSupply")}
              disabled
            />
            <FormControlLabel
              control={<Checkbox checked={order.isDemokit} name="isDemoKit" />}
              label="Demo kit"
              disabled
            />
          </Box>

          {cdlUser && (
            <Box
              sx={{
                display: "flex",

                alignItems: "center",
                px: 2,
              }}>
              <TextField
                id="comment"
                name="comment"
                label={t("siteComment")}
                multiline
                rows={3}
                sx={{ mr: 2, width: "70%" }}
                value={comment}
                onChange={(event) => {
                  setComment(event.target.value);
                }}
              />
              {adminUser && (
                <Button
                  variant="contained"
                  color="primary"
                  sx={{
                    height: "fit-content",
                    width: "30%",
                    padding: "16px 4px",
                  }}
                  onClick={async () => {
                    await updateOrderField(orderId, "comment", comment);
                    showSuccessAlert("The comment was successfully updated");
                  }}>
                  {t("updateComment")}
                </Button>
              )}
              {cdlUser && (
                <>
                  <TextField
                    id="cdlComment"
                    name="comment"
                    label={t("cdlComment")}
                    multiline
                    rows={3}
                    sx={{ m: 2, width: "70%" }}
                    value={cdlComment}
                    onChange={(event) => {
                      setCdlComment(event.target.value);
                    }}
                  />
                  <Button
                    variant="contained"
                    color="primary"
                    sx={{
                      height: "fit-content",
                      width: "30%",
                      padding: "16px 4px",
                    }}
                    onClick={async () => {
                      await updateOrderField(orderId, "cdlComment", cdlComment);
                      showSuccessAlert("The comment was successfully updated");
                    }}>
                    {t("updateComment")}
                  </Button>
                </>
              )}
            </Box>
          )}
          <EditQuantityDialog
            open={dialogOpen}
            handleClose={handleDialogClose}
            quantity={quantity}
            setQuantity={setQuantity}
            handleSaveEditQuantity={handleSaveEditQuantity}
          />
          <Box sx={{ p: 2 }}>
            <UserInfo
              title={t("personWhoPlacedOrder").toUpperCase()}
              user={order?.placedBy}
              date={formatDate(order?.orderDate)}
              onDoubleClick={() => {
                setNewConfirmationDate(order?.orderDate || null);
                setFieldToUpdate("orderDate");
                setConfirmationDateDialogOpen(true);
              }}
            />
            {order?.confirmedBy && (
              <UserInfo
                title={t("personWhoConfirmedOrder").toUpperCase()}
                user={order?.confirmedBy}
                date={
                  order?.confirmationDate
                    ? formatDate(order?.confirmationDate)
                    : null
                }
                onDoubleClick={() => {
                  setNewConfirmationDate(order?.confirmationDate || null);
                  setFieldToUpdate("confirmationDate");
                  setConfirmationDateDialogOpen(true);
                }}
              />
            )}
            {order.shipments &&
              order.shipments[0]?.confirmedDeliveryDate &&
              !cdlUser && (
                <UserInfo
                  title={t("personInChargeOfReception").toUpperCase()}
                  user={order.shipments[0].receivedBy}
                  date={formatDate(order.shipments[0]?.confirmedDeliveryDate)}
                />
              )}

            {/* OrderInfo Controls: "Placed" */}
            {/* ----------------------------------------------- */}
            {adminUser && order.status === "placed" && (
              <Grid container item xs={12} spacing={2} sx={{ mt: 2, mb: 2 }}>
                <Grid item xs={4}>
                  <Button
                    variant="contained"
                    color="secondary"
                    onClick={handleDeleteOrder}
                    fullWidth
                    sx={{ padding: "16px 8px" }}>
                    {t("deleteOrder")}
                  </Button>
                </Grid>

                <Grid item xs={4}>
                  <Button
                    variant="contained"
                    color="secondary"
                    onClick={cancelOrder}
                    fullWidth
                    sx={{ padding: "16px 8px" }}>
                    {t("cancelOrder")}
                  </Button>
                </Grid>

                <Grid item xs={4}>
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={confirmOrder}
                    fullWidth
                    sx={{ padding: "16px 8px" }}>
                    {t("confirmOrder")}
                  </Button>
                </Grid>
              </Grid>
            )}

            {/* OrderInfo Controls: "Confirmed" */}
            {/* ----------------------------------------------- */}
            {adminUser && order.status === "confirmed" && (
              <Grid container item xs={12} spacing={2} sx={{ mt: 2, mb: 2 }}>
                <Grid item xs={4}>
                  <Button
                    variant="contained"
                    color="secondary"
                    fullWidth
                    sx={{ padding: "16px 8px" }}
                    onClick={handleDeleteOrder}>
                    {t("deleteOrder")}
                  </Button>
                </Grid>

                <Grid item xs={4}>
                  <Button
                    variant="contained"
                    color="secondary"
                    fullWidth
                    sx={{ padding: "16px 8px" }}
                    onClick={cancelOrder}>
                    {t("cancelOrder")}
                  </Button>
                </Grid>
              </Grid>
            )}
          </Box>
        </div>

        <EditDateDialog
          open={confirmationDateDialogOpen}
          onClose={() => setConfirmationDateDialogOpen(false)}
          dateValue={newConfirmationDate}
          onDateChange={(newValue) => setNewConfirmationDate(newValue)}
          onSave={handleSaveDate}
          title={t("editDate")}
        />
      </Paper>
    </Grid>
  );
};
